import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api, CATEGORY_LABELS } from "../api";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const category = params.get("category") || "";

  useEffect(() => {
    api.get("/products")
      .then((r) => setProducts(r.data.products))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category) list = list.filter((p) => p.category === category);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)
      );
    }
    if (sort === "price_asc") list.sort((a, b) => a.price_cents - b.price_cents);
    else if (sort === "price_desc") list.sort((a, b) => b.price_cents - a.price_cents);
    else if (sort === "newest") list.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return list;
  }, [products, category, search, sort]);

  const chips = [{ id: "", label: "All" }, ...Object.entries(CATEGORY_LABELS).map(([id, label]) => ({ id, label }))];

  return (
    <div className="mx-auto max-w-7xl px-6 py-14 md:px-8 lg:py-20" data-testid="shop-page">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage">The shop</p>
        <h1 className="mt-3 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Every piece, pressed &amp; ready
        </h1>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-md">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              data-testid="shop-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search planners, trackers, templates…"
              className="w-full rounded-full border border-sage/20 bg-white/80 py-3.5 pl-11 pr-5 text-sm text-ink outline-none backdrop-blur transition-colors placeholder:text-ink/35 focus:border-sage"
            />
          </div>
          <select
            data-testid="shop-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-sage/20 bg-white/80 px-5 py-3 text-sm text-ink/80 outline-none focus:border-sage"
          >
            <option value="featured">Featured first</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
          </select>
        </div>
        <div className="mt-6 flex flex-wrap gap-2.5">
          {chips.map((c) => (
            <button
              key={c.id || "all"}
              data-testid={`shop-category-${c.id || "all"}`}
              onClick={() => {
                if (c.id) params.set("category", c.id);
                else params.delete("category");
                setParams(params);
              }}
              className={`rounded-full px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] transition-all duration-300 ${
                category === c.id
                  ? "bg-sage text-cream shadow-[0_10px_20px_-8px_rgba(59,87,75,0.5)]"
                  : "border border-sage/20 bg-white/60 text-ink/60 hover:border-sage hover:text-sage"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-product-grid">
        {filtered.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} />
        ))}
      </div>
      {!loading && filtered.length === 0 && (
        <p className="mt-16 text-center font-serif text-xl italic text-ink/50" data-testid="shop-empty-state">
          Nothing matches that yet — try another word or category.
        </p>
      )}
    </div>
  );
}
